import { assertUnreachable, requireNonNullable } from '@disy/lib/custom-error';

import { createToggletip } from 'ui/toggletip/toggletip';

import { Command } from 'cadenza/api-client/workbook-commands';
import type { AttributeCategory } from 'cadenza/repository/attribute-category';
import type { AttributeType } from 'cadenza/repository/attribute-type';
import { isFormattable } from 'cadenza/repository/attribute-type';
import type { MeasureAggregation } from 'cadenza/repository/model/measure-aggregation.gen';
import { getTargetAttributeType } from 'cadenza/repository/model/measure-aggregation.gen';
import type { SortType } from 'cadenza/repository/model/sort-type.gen';
import { getStore } from 'cadenza/store/cadenza-store';
import { getCommonFormats } from 'cadenza/ui/attribute-format-editor/attribute-format-utils';
import type { Primitive } from 'cadenza/utils/primitive';
import type { ActiveAttributeId } from 'cadenza/workbook/api/active-attribute-id';
import type { AttributeModificationKey } from 'cadenza/workbook/api/attribute-modification-key';
import type { ViewId } from 'cadenza/workbook/api/view-id';
import { viewTypeIcon } from 'cadenza/workbook/api/view-type';
import type { WorkbookAttributeDto } from 'cadenza/workbook/api/workbook-attribute-dto';
import { getChartCategoryAttributes, getChartValueAttributes } from 'cadenza/workbook/data-view/data-view-utils';
import { ChartDataViewSettingsKey } from 'cadenza/workbook/model/chart-data-view-settings-key.gen';
import { CombinationChartDataViewVisualizationKeys } from 'cadenza/workbook/model/combination-chart-visualization-types.gen';
import { CommonDataViewSettingsKey } from 'cadenza/workbook/model/common-data-view-settings-key.gen';
import type { DataViewId } from 'cadenza/workbook/model/data-view-id.gen';
import type { QueryExecutionStrategyType } from 'cadenza/workbook/query-execution-strategy-type';
import { executeCommand } from 'cadenza/workbook/workbook-core-reducers.state';
import {
  getDataViewById,
  getDataViewSetting,
  getDataViewSettings,
  getOnlyDataView,
  getViews,
  isCombinationChart,
  isInCombinationChart,
} from 'cadenza/workbook/workbook-core-selectors.state';
import { createDataViewAttributeModificationCommand } from 'cadenza/workbook/workbook-panel/settings/attribute-modification-utils';
import type { Settings } from 'cadenza/workbook/workbook-settings-util';
import { getSetting } from 'cadenza/workbook/workbook-settings-util';

import i18n from './combination-chart-utils.properties';

const SYNCHRONIZED_DATA_VIEW_SETTINGS_KEY = [
  // always
  'CATEGORY_AXIS_TITLE_ON',
  'CATEGORY_AXIS_TITLE_TEXT',
  'CATEGORY_AXIS_SCROLLBAR_ON',
  'VALUE_AXIS_SCROLLBAR_ON',
  'LEGEND_ON',
  'FONT_DISPLAY',
  'QUERY_EXECUTION_STRATEGY',
  'MAX_DATA_RECORDS_STRATEGY',
  'SELECTION_ENABLED',
  'SELECTION_ONE_CLICK_FILTERING_ENABLED',
  'VALUE_AXIS_MERGE_ON',
  // conditionally
  'CATEGORY_AXIS_LABELS_ON',
  'VALUE_AXIS_LABELS_ON',
  'VALUE_AXIS_LOCATION',
  'VALUE_AXIS_TITLE_ON',
  'VALUE_AXIS_TITLE_TEXT',
  'VALUE_AXIS_MIN_DISPLAYED_VALUE',
  'VALUE_AXIS_MAX_DISPLAYED_VALUE',
] satisfies (ChartDataViewSettingsKey | CommonDataViewSettingsKey)[];

export type SynchronizedChartDataViewSettingsKey = (typeof SYNCHRONIZED_DATA_VIEW_SETTINGS_KEY)[number];

export const SYNCHRONIZED_ATTRIBUTE_MODIFICATION_KEY = ['FORMAT', 'SORT_ORDER'] satisfies AttributeModificationKey[];
export type SynchronizedAttributeModificationKey = (typeof SYNCHRONIZED_ATTRIBUTE_MODIFICATION_KEY)[number];

type SynchronizedSettingsKey = SynchronizedChartDataViewSettingsKey | SynchronizedAttributeModificationKey;

export function canBeCombined(viewId: ViewId) {
  return (
    !isCombinationChart(getStore().getState(), viewId) &&
    CombinationChartDataViewVisualizationKeys.values.includes(
      getOnlyDataView(getStore().getState(), viewId).visualizationKey,
    )
  );
}

export function createSynchronizedToggleTip(
  dataViewId: DataViewId,
  settingKey: SynchronizedSettingsKey,
  attributeCategory?: AttributeCategory,
) {
  return (
      findSynchronizedDataViewIds(dataViewId, settingKey, {
        attributeCategory,
      }).length
    ) ?
      createToggletip({
        size: 'xs',
        toggletipPlacement: 'bottom-start',
        icon: viewTypeIcon('CHART_COMBINATION', { size: 'xs' }),
        toggletip: i18n('synchronizedTooltip'),
        slot: 'label-buttons',
      })
    : undefined;
}

function isMeasureAttributesSimilar(first?: WorkbookAttributeDto, second?: WorkbookAttributeDto) {
  return (
    // undefined attributes are assumed similar -> necessary for synchronizing settings when combining
    !first ||
    !second ||
    // attributes with same base id are considered similar
    first.baseId === second.baseId
  );
}

export function toPersistenceKey(settingKey: SynchronizedChartDataViewSettingsKey) {
  const isChartSettingKey = ChartDataViewSettingsKey.values.some((k) => k === settingKey);
  if (isChartSettingKey) {
    return ChartDataViewSettingsKey.toPersistenceKey(settingKey as ChartDataViewSettingsKey);
  }
  return CommonDataViewSettingsKey.toPersistenceKey(settingKey as CommonDataViewSettingsKey);
}

export function setSynchronizedDataViewsSetting(
  dataViewId: DataViewId,
  settingKey: SynchronizedChartDataViewSettingsKey,
  value: Primitive,
) {
  return getStore().dispatch(
    executeCommand(
      ...[dataViewId, ...findSynchronizedDataViewIds(dataViewId, settingKey)].map((id) =>
        Command.CHANGE_DATA_VIEW_SETTINGS({
          dataViewId: id,
          settings: { [toPersistenceKey(settingKey)]: value },
        }),
      ),
    ),
  );
}

function getOnDemandSettingValue(
  dataViewId: DataViewId,
  synchronizedDataViewIds: DataViewId[],
  persistenceKey: string,
) {
  const toMoveDataViewSetting = getDataViewSetting<QueryExecutionStrategyType>(
    getStore().getState(),
    dataViewId,
    persistenceKey,
  );

  if (toMoveDataViewSetting === 'onDemand') {
    return 'onDemand';
  }

  const synchronizedSetting = getDataViewSetting<QueryExecutionStrategyType>(
    getStore().getState(),
    synchronizedDataViewIds[0],
    persistenceKey,
  );

  return synchronizedSetting ?? null;
}

export function createSynchronizeSettingsCommands(
  dataViewId: DataViewId,
  candidateDataViewIds = getDefaultCandidateDataViewsForSynchronization(dataViewId),
) {
  return [
    ...createSynchronizeDataViewSettingsCommands(dataViewId, candidateDataViewIds),
    ...createCommandToSetOnDemandOnCandidates(dataViewId, candidateDataViewIds),
    ...createSynchronizeAttributeModificationCommands(dataViewId, candidateDataViewIds),
  ];
}

/**
 * Creates the commands to implicitly synchronize deviated attribute modifications
 */
function createSynchronizeAttributeModificationCommands(dataViewId: DataViewId, candidateDataViewIds: DataViewId[]) {
  const commands: Command[] = [];
  SYNCHRONIZED_ATTRIBUTE_MODIFICATION_KEY.map((modificationKey) => ({
    modificationKey,
    attributeCategories: (modificationKey === 'FORMAT' ?
      // Format of measure is also synchronized
      ['DIMENSION', 'MEASURE']
    : ['DIMENSION']) satisfies AttributeCategory[],
  })).forEach(({ modificationKey, attributeCategories }) => {
    attributeCategories.forEach((attributeCategory) => {
      const synchronizedDataViews = findSynchronizedDataViewIds(dataViewId, modificationKey, {
        candidateDataViewIds,
        attributeCategory,
      });
      if (synchronizedDataViews.length) {
        const command = createAttributeModificationCommand(
          dataViewId,
          synchronizedDataViews[0],
          modificationKey,
          attributeCategory,
        );
        if (command) {
          commands.push(command);
        }
      }
    });
  });
  return commands;
}

/**
 * Creates the commands to synchronize change in the attribute modifications due to user input
 */
export function createSynchronizedAttributeModificationCommandsFromUserInput(
  attribute: WorkbookAttributeDto,
  modifications: [AttributeModificationKey, string | null][],
  dataViewId: DataViewId,
) {
  const synchronizeCommands: Command[] = [];
  if (
    (attribute.category === 'DIMENSION' || attribute.category === 'MEASURE') &&
    isInCombinationChart(getStore().getState(), dataViewId)
  ) {
    for (const [modification, value] of modifications) {
      if (isSynchronizedAttributeModificationKey(modification)) {
        for (const synchronizedDataViewId of findSynchronizedDataViewIds(dataViewId, modification, {
          attributeCategory: attribute.category,
        })) {
          const command = createAttributeModificationCommand(
            dataViewId,
            synchronizedDataViewId,
            modification,
            attribute.category,
            value,
          );
          if (command) {
            synchronizeCommands.push(command);
          }
        }
      }
    }
  }
  return synchronizeCommands;
}

function createAttributeModificationCommand(
  dataViewId: DataViewId,
  synchronizedDataViewId: DataViewId,
  modification: SynchronizedAttributeModificationKey,
  category: AttributeCategory,
  // value is present for user interaction only, in which case the command is instead set for synchronizedDataViewId
  value?: string | null,
) {
  const attributes = validateAndGetAttributes(dataViewId, synchronizedDataViewId, category);
  if (!attributes) {
    return;
  }
  const { attribute, synchronizedAttribute } = attributes;
  const forSynchronizedDataView = value !== undefined;
  const commandAttribute = forSynchronizedDataView ? synchronizedAttribute : attribute;
  const commandDataViewId = forSynchronizedDataView ? synchronizedDataViewId : dataViewId;
  switch (modification) {
    case 'FORMAT': {
      // synchronize only for same hierarchyType
      if (attribute.hierarchyType === synchronizedAttribute.hierarchyType) {
        const newFormat =
          forSynchronizedDataView ?
            // if the value was reset, use defaultFormat.
            (value ?? getDefaultFormat(attribute, attribute.measureAggregation))
          : (synchronizedAttribute.format ??
            getDefaultFormat(synchronizedAttribute, synchronizedAttribute.measureAggregation));
        const oldFormat =
          commandAttribute.format ?? getDefaultFormat(commandAttribute, commandAttribute.measureAggregation);
        if (newFormat !== oldFormat) {
          return createDataViewAttributeModificationCommand(
            commandDataViewId,
            commandAttribute.id as ActiveAttributeId,
            modification,
            newFormat,
          );
        }
      }
      return;
    }
    case 'SORT_ORDER': {
      // only DIMENSION is allowed
      if (category !== 'DIMENSION') {
        return;
      }
      const newSortOrder =
        (forSynchronizedDataView ?
          // if the value was reset, use default
          (value ?? attribute.defaultSortDirection)
        : (synchronizedAttribute.sortDirection ?? synchronizedAttribute.defaultSortDirection)) ?? 'ASCENDING';
      const oldSortOrder = commandAttribute.sortDirection ?? commandAttribute.defaultSortDirection ?? 'ASCENDING';
      if (newSortOrder !== oldSortOrder) {
        return Command.SORT_DATA_VIEW_BY_ATTRIBUTE({
          dataViewId: commandDataViewId,
          attributeId: commandAttribute.id as ActiveAttributeId,
          order: newSortOrder as SortType,
        });
      }
      return;
    }
  }
  assertUnreachable(modification);
}

function getDefaultFormat(attribute: WorkbookAttributeDto, measureAggregation?: MeasureAggregation) {
  if (attribute.defaultFormat) {
    return attribute.defaultFormat;
  }
  // if no defaultFormat is present, use a default format based on the type which can happen for local attributes (Duplicate as Measure)
  const actualAttributeType =
    measureAggregation ? getTargetAttributeType(attribute.type, measureAggregation!) : attribute.type;
  if (isFormattable(actualAttributeType)) {
    return getCommonFormats(actualAttributeType)[0];
  }
}

function validateAndGetAttributes(
  dataViewId: DataViewId,
  synchronizedDataViewId: DataViewId,
  category: AttributeCategory,
) {
  if (category === 'DIMENSION') {
    const attribute = getChartCategoryAttributes(dataViewId).at(0);
    const synchronizedAttribute = getChartCategoryAttributes(synchronizedDataViewId).at(0);
    const shouldSynchronize =
      attribute &&
      synchronizedAttribute &&
      // no need to synchronize between incompatible types which will show error state anyway
      isCategoryAttributeTypeCompatible(attribute.type, synchronizedAttribute.type);
    if (shouldSynchronize) {
      return { attribute, synchronizedAttribute };
    }
  } else if (category === 'MEASURE') {
    const attribute = getChartValueAttributes(dataViewId).at(0);
    const synchronizedAttribute = getChartValueAttributes(synchronizedDataViewId).at(0);
    if (attribute && synchronizedAttribute) {
      return { attribute, synchronizedAttribute };
    }
  }
}

function isCategoryAttributeTypeCompatible(first: AttributeType, second: AttributeType) {
  const supportedDifferentTypes: AttributeType[] = ['INT', 'LONG'];
  return first === second || (supportedDifferentTypes.includes(first) && supportedDifferentTypes.includes(second));
}

/**
 * If onDemand is enabled on any of the dataView, it should be enabled for all synchronized dataViews.
 * So, this function creates the command to set onDemand on all the candidate dataViews, if the toSynchronizeDataView has onDemand enabled.
 */
function createCommandToSetOnDemandOnCandidates(
  toSynchronizeDataViewId: DataViewId,
  candidateDataViewIds: DataViewId[],
) {
  const persistenceKey = toPersistenceKey('QUERY_EXECUTION_STRATEGY');
  const onDemandString: QueryExecutionStrategyType = 'onDemand';
  const isOnDemandEnabled =
    getDataViewSetting(getStore().getState(), toSynchronizeDataViewId, persistenceKey) === onDemandString;

  return isOnDemandEnabled ?
      candidateDataViewIds
        // create command only if not currently is onDemand
        .filter(
          (dataViewId) => getDataViewSetting(getStore().getState(), dataViewId, persistenceKey) !== onDemandString,
        )
        .map((dataViewId) =>
          Command.CHANGE_DATA_VIEW_SETTINGS({
            dataViewId,
            settings: { [persistenceKey]: onDemandString },
          }),
        )
    : [];
}

function createSynchronizeDataViewSettingsCommands(dataViewId: DataViewId, candidateDataViewIds: DataViewId[]) {
  const synchronizedSettings: Settings = {};
  SYNCHRONIZED_DATA_VIEW_SETTINGS_KEY.forEach((settingKey) => {
    const synchronizedDataViewIds = findSynchronizedDataViewIds(dataViewId, settingKey, { candidateDataViewIds });
    if (synchronizedDataViewIds.length) {
      const persistenceKey = toPersistenceKey(settingKey);

      // If onDemand is enabled on the source dataView, it should be enabled for all synchronized dataViews
      if (settingKey === 'QUERY_EXECUTION_STRATEGY') {
        synchronizedSettings[persistenceKey] = getOnDemandSettingValue(
          dataViewId,
          synchronizedDataViewIds,
          persistenceKey,
        );
      } else {
        synchronizedSettings[persistenceKey] =
          getDataViewSetting(
            getStore().getState(),
            // picking one the synchronizedDataViewIds is enough as all synchronizedDataViewIds will have same setting value
            synchronizedDataViewIds[0],
            persistenceKey,
          ) ?? null; // explicitly set null for undefined values, so that the value is cleared because undefined is not sent to backend
      }
    }
  });

  const toSynchronizeSettings = filterOnlyDifferentSettings(
    getDataViewSettings(
      getStore().getState(),
      dataViewId,
      SYNCHRONIZED_DATA_VIEW_SETTINGS_KEY.map((settingKey) => toPersistenceKey(settingKey)),
    ),
    synchronizedSettings,
  );
  return Object.keys(toSynchronizeSettings).length ?
      [
        Command.CHANGE_DATA_VIEW_SETTINGS({
          dataViewId,
          settings: toSynchronizeSettings,
        }),
      ]
    : [];
}

function filterOnlyDifferentSettings(currentSettings: Settings, synchronizedSettings: Settings) {
  return (
    Object.entries(synchronizedSettings)
      // allow both null and undefined for null values
      .filter(([key, value]) => (value === null ? currentSettings[key] != null : currentSettings[key] !== value))
      .reduce<Settings>((settings, [key, value]) => {
        settings[key] = value;
        return settings;
      }, {})
  );
}

export function findSynchronizedDataViewIds(
  dataViewId: DataViewId,
  settingKey: SynchronizedSettingsKey,
  {
    candidateDataViewIds = getDefaultCandidateDataViewsForSynchronization(dataViewId),
    attributeCategory,
  }: {
    candidateDataViewIds?: DataViewId[];
    attributeCategory?: AttributeCategory;
  } = {},
) {
  switch (settingKey) {
    // always synchronized settings are synchronized for all data-views
    case 'CATEGORY_AXIS_TITLE_ON':
    case 'CATEGORY_AXIS_TITLE_TEXT':
    case 'CATEGORY_AXIS_SCROLLBAR_ON':
    case 'VALUE_AXIS_SCROLLBAR_ON':
    case 'LEGEND_ON':
    case 'FONT_DISPLAY':
    case 'QUERY_EXECUTION_STRATEGY':
    case 'MAX_DATA_RECORDS_STRATEGY':
    case 'SELECTION_ENABLED':
    case 'SELECTION_ONE_CLICK_FILTERING_ENABLED':
    case 'VALUE_AXIS_MERGE_ON':
      return candidateDataViewIds;
    case 'FORMAT': {
      if (attributeCategory === 'DIMENSION') {
        return getSynchronizedDataViewsForCategoryAxisSettings(dataViewId, candidateDataViewIds);
      } else if (attributeCategory === 'MEASURE') {
        return getSynchronizedDataViewsForValueAxisSettings(dataViewId, candidateDataViewIds);
      }
      return [];
    }
    case 'SORT_ORDER': {
      // only DIMENSION is supported
      if (attributeCategory === 'DIMENSION') {
        return getSynchronizedDataViewsForCategoryAxisSettings(dataViewId, candidateDataViewIds);
      }
      return [];
    }
    case 'CATEGORY_AXIS_LABELS_ON': {
      return getSynchronizedDataViewsForCategoryAxisSettings(dataViewId, candidateDataViewIds);
    }
    case 'VALUE_AXIS_LABELS_ON':
    case 'VALUE_AXIS_TITLE_ON':
    case 'VALUE_AXIS_TITLE_TEXT':
    case 'VALUE_AXIS_MIN_DISPLAYED_VALUE':
    case 'VALUE_AXIS_MAX_DISPLAYED_VALUE':
    case 'VALUE_AXIS_LOCATION':
      // value axis settings are synchronized only among data-views having similar measure attributes
      return getSynchronizedDataViewsForValueAxisSettings(dataViewId, candidateDataViewIds);
  }
  assertUnreachable(settingKey);
}

function getSynchronizedDataViewsForCategoryAxisSettings(dataViewId: DataViewId, candidateDataViewIds: DataViewId[]) {
  const categoryAttribute = getChartCategoryAttributes(dataViewId).at(0);
  if (categoryAttribute && categoryAttribute.type === 'DATE') {
    // not synchronized for date which has individual axis for each data-view
    return [];
  }
  return candidateDataViewIds;
}

export function getSynchronizedDataViewsForValueAxisSettings(
  dataViewId: DataViewId,
  candidateDataViewIds = getDefaultCandidateDataViewsForSynchronization(dataViewId),
) {
  // synchronized when value axis is merged
  return shouldMergeValueAxis(dataViewId) ? candidateDataViewIds : [];
}

export function shouldMergeValueAxis(dataViewId: DataViewId) {
  return getSetting(
    getDataViewById(getStore().getState(), dataViewId),
    ChartDataViewSettingsKey.toPersistenceKey('VALUE_AXIS_MERGE_ON'),
    getAutoValueAxisMergeSetting(dataViewId),
  );
}

/**
 * By default, we merge the value axis if all the measure attributes are similar.
 * So, return true if all measure attributes are similar.
 */
export function getAutoValueAxisMergeSetting(dataViewId: DataViewId) {
  const measureAttribute = getChartValueAttributes(dataViewId).at(0);
  return getDefaultCandidateDataViewsForSynchronization(dataViewId).every((id) =>
    isMeasureAttributesSimilar(measureAttribute, getChartValueAttributes(id).at(0)),
  );
}

function getDefaultCandidateDataViewsForSynchronization(dataViewId: DataViewId) {
  // cannot use getViewByDataViewId selector because the dataViewId can be from different worksheet when combining views from different worksheet
  const view = requireNonNullable(getViews(getStore().getState()).find((v) => v.dataViews.includes(dataViewId)));
  return view.dataViews.filter((id) => id !== dataViewId);
}

export function isSynchronizedDataViewSettingsKey(
  settingsKey: ChartDataViewSettingsKey | CommonDataViewSettingsKey,
): settingsKey is SynchronizedChartDataViewSettingsKey {
  return SYNCHRONIZED_DATA_VIEW_SETTINGS_KEY.includes(settingsKey as SynchronizedChartDataViewSettingsKey);
}

function isSynchronizedAttributeModificationKey(
  modificationKey: AttributeModificationKey,
): modificationKey is SynchronizedAttributeModificationKey {
  return SYNCHRONIZED_ATTRIBUTE_MODIFICATION_KEY.includes(modificationKey as SynchronizedAttributeModificationKey);
}
